import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { formatCompanyDate, formatCompanyDateRange } from '../utils/companyDateFormat';
import { X, Upload, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { scheduleShiftImportApi } from '../services/api';
import type { ScheduleShiftImportColumnMapping, ScheduleShiftImportValidateResponse } from '../types';
import { ScheduleShiftImportRowsPreviewTable } from './ScheduleShiftImportRowsPreview';
import {
  detectScheduleImportPattern,
  type CellMatrix,
  type GridWeekBlock,
  type ImportPatternResult,
  type MultiWeekImportSummary,
  unpivotGridWeekBlock,
  collectAllImportRowsFromMatrix,
  buildMultiWeekImportSummary,
  parseImportDate,
} from '../utils/scheduleShiftImportPatterns';

const mappingStorageKey = (locationId: number, scheduleId: number) =>
  `staffly.scheduleShiftImport.mapping.v1.${locationId}.${scheduleId}`;

type Step = 'upload' | 'mapping' | 'grid-confirm' | 'multi-week-confirm' | 'preview' | 'done';

export interface ImportScheduleFromFileModalProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  scheduleId: number;
  /** Inclusive yyyy-MM-dd for the scheduling week */
  allowedWeekStartDate: string;
  allowedWeekEndDate: string;
  /** Shifts already on this schedule for the week (informational only; import does not clear the week) */
  existingShiftCount?: number;
  onImportCommitted?: (result?: { placeholderIds?: number[] }) => void;
}

const MAPPING_FIELDS: { key: keyof ScheduleShiftImportColumnMapping; label: string; required: boolean }[] = [
  { key: 'employeeName', label: 'Employee or placeholder name', required: true },
  { key: 'date', label: 'Date', required: true },
  { key: 'startTime', label: 'Start time', required: true },
  { key: 'endTime', label: 'End time', required: true },
];

type SheetData = {
  headers: string[];
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>;
};

function parseMatrixToSheetData(matrix: (string | number | undefined)[][]): SheetData | null {
  if (!matrix.length) return null;

  const rawHeaders = matrix[0].map((c) => String(c ?? '').trim());
  if (!rawHeaders.some(Boolean)) return null;

  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  let dataRowOrdinal = 0;
  for (let r = 1; r < matrix.length; r++) {
    const line = matrix[r];
    const cells: Record<string, string> = {};
    let any = false;
    rawHeaders.forEach((h, i) => {
      if (!h) return;
      const v = line[i];
      const cellStr = v === undefined || v === null ? '' : String(v).trim();
      cells[h] = cellStr;
      if (cellStr) any = true;
    });
    if (any) {
      dataRowOrdinal += 1;
      rows.push({ rowNumber: dataRowOrdinal, cells });
    }
  }

  return { headers: rawHeaders.filter(Boolean), rows };
}

function pickMappedColumn(stored: string | undefined | null, headers: string[]): string {
  if (stored == null) return '';
  const t = String(stored).trim();
  if (t === '') return '';
  const hit = headers.find((h) => h === t);
  return hit !== undefined ? hit : '';
}

function intersectMappingWithHeaders(
  m: Partial<ScheduleShiftImportColumnMapping>,
  headers: string[]
): ScheduleShiftImportColumnMapping {
  return {
    employeeName: pickMappedColumn(m.employeeName, headers),
    date: pickMappedColumn(m.date, headers),
    startTime: pickMappedColumn(m.startTime, headers),
    endTime: pickMappedColumn(m.endTime, headers),
  };
}

function saveStoredMapping(locationId: number, scheduleId: number, m: ScheduleShiftImportColumnMapping) {
  try {
    localStorage.setItem(mappingStorageKey(locationId, scheduleId), JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

function loadStoredMapping(locationId: number, scheduleId: number): Partial<ScheduleShiftImportColumnMapping> {
  try {
    const raw = localStorage.getItem(mappingStorageKey(locationId, scheduleId));
    if (!raw) return {};
    return JSON.parse(raw) as Partial<ScheduleShiftImportColumnMapping>;
  } catch {
    return {};
  }
}

/** Best-effort parse for week check on date column */
function tryParseImportCellToDate(raw: string): Date | null {
  return parseImportDate(raw);
}

function formatImportDateDisplay(raw: string | undefined | null): string {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return formatCompanyDate(trimmed) || trimmed;
  }
  const parsed = parseImportDate(trimmed);
  if (!parsed) return trimmed;
  const iso = `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
  return formatCompanyDate(iso) || trimmed;
}

function findFirstDateOutsideAllowedWeek(
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>,
  dateCol: string,
  weekStart: string,
  weekEnd: string
): string | null {
  for (const r of rows) {
    const dateRaw = r.cells[dateCol]?.trim();
    if (!dateRaw) continue;
    const d = tryParseImportCellToDate(dateRaw);
    if (!d) {
      return `Row ${r.rowNumber}: could not parse date.`;
    }
    const inStr = format(d, 'yyyy-MM-dd');
    if (inStr < weekStart || inStr > weekEnd) {
      const weekLabel = formatCompanyDateRange(weekStart, weekEnd) || `${weekStart} – ${weekEnd}`;
      return `Row ${r.rowNumber}: date must fall within the current week (${weekLabel}).`;
    }
  }
  return null;
}

export const ImportScheduleFromFileModal: React.FC<ImportScheduleFromFileModalProps> = ({
  open,
  onClose,
  locationId,
  scheduleId,
  allowedWeekStartDate,
  allowedWeekEndDate,
  existingShiftCount,
  onImportCommitted,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetsByName, setSheetsByName] = useState<Record<string, SheetData>>({});
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetRows, setSheetRows] = useState<Array<{ rowNumber: number; cells: Record<string, string> }>>([]);
  const [mapping, setMapping] = useState<ScheduleShiftImportColumnMapping>({
    employeeName: '',
    date: '',
    startTime: '',
    endTime: '',
  });
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validateResult, setValidateResult] = useState<ScheduleShiftImportValidateResponse | null>(null);
  const [overlapResolution, setOverlapResolution] = useState<'Skip' | 'Overwrite'>('Skip');
  const [error, setError] = useState<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const commitInProgressRef = useRef(false);
  const [rawSheetsByName, setRawSheetsByName] = useState<Record<string, CellMatrix>>({});
  const [importPattern, setImportPattern] = useState<ImportPatternResult | null>(null);
  const [gridWeekBlocks, setGridWeekBlocks] = useState<GridWeekBlock[]>([]);
  const [selectedWeekBlockKey, setSelectedWeekBlockKey] = useState('');
  const [multiWeekSummary, setMultiWeekSummary] = useState<MultiWeekImportSummary | null>(null);
  const [importScope, setImportScope] = useState<'current-week' | 'all-weeks'>('current-week');

  const headerOptions = useMemo(() => ['', ...headers], [headers]);

  const allowedWeekLabel = useMemo(() => {
    try {
      const a = parseISO(allowedWeekStartDate);
      const b = parseISO(allowedWeekEndDate);
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
      return formatCompanyDateRange(a, b);
    } catch {
      return '';
    }
  }, [allowedWeekStartDate, allowedWeekEndDate]);

  useEffect(() => {
    if (!open) return;
    const stored = loadStoredMapping(locationId, scheduleId);
    setMapping(intersectMappingWithHeaders(stored, []));
    setStep('upload');
    setFileName('');
    setSheetNames([]);
    setSheetsByName({});
    setSelectedSheetName('');
    setHeaders([]);
    setSheetRows([]);
    setValidateResult(null);
    setOverlapResolution('Skip');
    setError(null);
    setSourceFile(null);
    commitInProgressRef.current = false;
    setRawSheetsByName({});
    setImportPattern(null);
    setGridWeekBlocks([]);
    setSelectedWeekBlockKey('');
    setMultiWeekSummary(null);
    setImportScope('current-week');
  }, [open, locationId, scheduleId]);

  const applyImportPattern = useCallback(
    (matrix: CellMatrix, patternOverride?: ImportPatternResult | null) => {
      const detected =
        patternOverride ??
        detectScheduleImportPattern(matrix, {
          allowedWeekStart: allowedWeekStartDate,
          allowedWeekEnd: allowedWeekEndDate,
        });

      if (!detected) {
        setImportPattern(null);
        setGridWeekBlocks([]);
        setSelectedWeekBlockKey('');
        return null;
      }

      setImportPattern(detected);
      setSheetRows(detected.rows);
      setMapping(detected.mapping);
      setHeaders(Object.values(detected.mapping).filter(Boolean));

      if (detected.gridWeekBlocks?.length) {
        setGridWeekBlocks(detected.gridWeekBlocks);
        const block = detected.selectedWeekBlock ?? detected.gridWeekBlocks[0]!;
        setSelectedWeekBlockKey(block.label);
      } else {
        setGridWeekBlocks([]);
        setSelectedWeekBlockKey('');
      }

      return detected;
    },
    [allowedWeekStartDate, allowedWeekEndDate]
  );

  const gridTimeMode = useMemo((): 'short' | 'verbose' => {
    if (importPattern?.patternId === 'grid-verbose-time') return 'verbose';
    if (importPattern?.patternId === 'grid-short-time') return 'short';
    return 'verbose';
  }, [importPattern?.patternId]);

  const previewImportableCount = useMemo(() => {
    if (!validateResult) return 0;
    const v = validateResult.validRowCount ?? 0;
    const o = validateResult.overlapRowCount ?? 0;
    return overlapResolution === 'Overwrite' ? v + o : v;
  }, [validateResult, overlapResolution]);

  const onSheetChange = useCallback(
    (sheetName: string) => {
      setSelectedSheetName(sheetName);
      const matrix = rawSheetsByName[sheetName];
      if (!matrix) return;

      const detected = applyImportPattern(matrix);
      if (detected?.patternId === 'flat-columns') {
        const data = sheetsByName[sheetName];
        if (data) {
          setHeaders(data.headers);
          setSheetRows(data.rows);
          const stored = loadStoredMapping(locationId, scheduleId);
          setMapping(intersectMappingWithHeaders(stored, data.headers));
        }
        setStep('mapping');
      } else if (detected) {
        setStep('grid-confirm');
      } else {
        const data = sheetsByName[sheetName];
        if (data) {
          setHeaders(data.headers);
          setSheetRows(data.rows);
        }
        setStep('mapping');
      }
    },
    [rawSheetsByName, sheetsByName, applyImportPattern, locationId, scheduleId]
  );

  const onWeekBlockChange = useCallback(
    (label: string) => {
      setSelectedWeekBlockKey(label);
      const block = gridWeekBlocks.find((b) => b.label === label);
      const matrix = rawSheetsByName[selectedSheetName];
      if (!block || !matrix) return;
      const result = unpivotGridWeekBlock(
        matrix,
        block,
        gridTimeMode,
        allowedWeekStartDate,
        allowedWeekEndDate
      );
      setImportPattern(result);
      setSheetRows(result.rows);
    },
    [
      gridWeekBlocks,
      rawSheetsByName,
      selectedSheetName,
      gridTimeMode,
      allowedWeekStartDate,
      allowedWeekEndDate,
    ]
  );

  const parseFile = useCallback(
    (file: File) => {
      setError(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const byName: Record<string, SheetData> = {};
          const rawByName: Record<string, CellMatrix> = {};
          const names: string[] = [];

          for (const sheetName of wb.SheetNames) {
            const sheet = wb.Sheets[sheetName];
            // Keep Excel date serials (e.g. 46266 = 1 Sep 2026). Formatted text like
            // "9/1/26" is read as day/month and misses the September week, so a rota
            // grid is rejected and the dialog jumps straight to manual mapping.
            const matrix = XLSX.utils.sheet_to_json<(string | number | undefined)[]>(sheet, {
              header: 1,
              defval: '',
              raw: true,
            }) as CellMatrix;

            rawByName[sheetName] = matrix;

            const parsed = parseMatrixToSheetData(matrix);
            if (parsed && parsed.headers.length > 0) {
              byName[sheetName] = parsed;
              names.push(sheetName);
            } else if (matrix.length > 0) {
              names.push(sheetName);
            }
          }

          if (names.length === 0) {
            setError('No sheet with rota or column data was found.');
            return;
          }

          setSheetsByName(byName);
          setRawSheetsByName(rawByName);
          setSheetNames(names);
          const firstName = names[0]!;
          setSelectedSheetName(firstName);
          setFileName(file.name);
          setSourceFile(file);

          const firstMatrix = rawByName[firstName]!;
          const detected = applyImportPattern(firstMatrix);
          if (detected?.patternId === 'flat-columns') {
            const firstData = byName[firstName];
            if (firstData) {
              setHeaders(firstData.headers);
              setSheetRows(firstData.rows);
              const stored = loadStoredMapping(locationId, scheduleId);
              setMapping(intersectMappingWithHeaders(stored, firstData.headers));
            }
            setStep('mapping');
          } else if (detected) {
            setStep('grid-confirm');
          } else if (byName[firstName]) {
            const firstData = byName[firstName]!;
            setHeaders(firstData.headers);
            setSheetRows(firstData.rows);
            setStep('mapping');
          } else {
            setError('Could not detect a supported rota layout in this file.');
          }
        } catch {
          setError('Could not read this Excel file.');
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [locationId, scheduleId, applyImportPattern]
  );

  const buildPayloadRows = useCallback(() => {
    return sheetRows.map((r) => ({
      rowNumber: r.rowNumber,
      cells: r.cells,
    }));
  }, [sheetRows]);

  const resolveImportParams = useCallback(
    (scope: 'current-week' | 'all-weeks') => {
      if (scope === 'all-weeks' && multiWeekSummary) {
        return {
          rows: multiWeekSummary.allRows.map((r) => ({ rowNumber: r.rowNumber, cells: r.cells })),
          allowedWeekStartDate: multiWeekSummary.fullRangeStart,
          allowedWeekEndDate: multiWeekSummary.fullRangeEnd,
        };
      }

      if (multiWeekSummary?.hasOtherWeeks) {
        let ordinal = 0;
        const filtered = multiWeekSummary.allRows
          .filter((r) => {
            const dateRaw = r.cells[mapping.date]?.trim();
            if (!dateRaw) return false;
            const d = tryParseImportCellToDate(dateRaw);
            if (!d) return false;
            const inStr = format(d, 'yyyy-MM-dd');
            return inStr >= allowedWeekStartDate && inStr <= allowedWeekEndDate;
          })
          .map((r) => {
            ordinal += 1;
            return { rowNumber: ordinal, cells: r.cells };
          });
        return {
          rows: filtered,
          allowedWeekStartDate,
          allowedWeekEndDate,
        };
      }

      return {
        rows: buildPayloadRows(),
        allowedWeekStartDate,
        allowedWeekEndDate,
      };
    },
    [multiWeekSummary, buildPayloadRows, mapping.date, allowedWeekStartDate, allowedWeekEndDate]
  );

  const runValidate = async (scope: 'current-week' | 'all-weeks') => {
    const { rows, allowedWeekStartDate: weekStart, allowedWeekEndDate: weekEnd } = resolveImportParams(scope);

    const weekErr = findFirstDateOutsideAllowedWeek(rows, mapping.date, weekStart, weekEnd);
    if (weekErr) {
      setError(weekErr);
      return;
    }

    saveStoredMapping(locationId, scheduleId, mapping);

    setValidating(true);
    setError(null);
    try {
      const res = await scheduleShiftImportApi.importValidate(
        {
          locationId,
          scheduleId,
          fileName: fileName || undefined,
          mapping: { ...mapping },
          rows,
          allowedWeekStartDate: weekStart,
          allowedWeekEndDate: weekEnd,
        },
        sourceFile
      );
      setValidateResult(res);
      setImportScope(scope);
      setStep('preview');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || (err as Error).message || 'Validation failed.');
    } finally {
      setValidating(false);
    }
  };

  const handleValidate = async () => {
    if (!mapping.employeeName || !mapping.date || !mapping.startTime || !mapping.endTime) {
      setError('Map all required fields to spreadsheet columns.');
      return;
    }

    const matrix = rawSheetsByName[selectedSheetName];
    const allRows =
      matrix && importPattern
        ? collectAllImportRowsFromMatrix(matrix, importPattern, gridTimeMode)
        : sheetRows;

    const summary = buildMultiWeekImportSummary(
      allRows,
      mapping.date,
      allowedWeekStartDate,
      allowedWeekEndDate,
      importPattern?.gridWeekBlocks
    );

    if (summary.hasOtherWeeks) {
      setMultiWeekSummary(summary);
      setStep('multi-week-confirm');
      return;
    }

    await runValidate('current-week');
  };

  const handleMultiWeekChoice = async (scope: 'current-week' | 'all-weeks') => {
    setImportScope(scope);
    await runValidate(scope);
  };

  const handleCommit = async () => {
    if (!validateResult?.batchId) return;
    if (commitInProgressRef.current) return;
    commitInProgressRef.current = true;
    setCommitting(true);
    setError(null);
    try {
      const committed = await scheduleShiftImportApi.importCommit(validateResult.batchId, overlapResolution);
      setStep('done');
      onImportCommitted?.({ placeholderIds: committed.placeholderIds ?? [] });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || (err as Error).message || 'Import failed.');
    } finally {
      commitInProgressRef.current = false;
      setCommitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import from file</h2>
              {allowedWeekLabel ? (
                <p className="mt-0.5 text-xs text-gray-500">
                  Default import week: {allowedWeekLabel}. Other weeks in the file can be included after confirmation.
                </p>
              ) : null}
            </div>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          {(step === 'mapping' || step === 'grid-confirm') && sheetNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <label htmlFor="import-schedule-sheet" className="text-sm font-medium text-gray-700">
                Sheet
              </label>
              <select
                id="import-schedule-sheet"
                value={selectedSheetName}
                onChange={(e) => onSheetChange(e.target.value)}
                className="min-w-[12rem] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              {step === 'grid-confirm' && gridWeekBlocks.length > 1 ? (
                <>
                  <label htmlFor="import-schedule-week" className="text-sm font-medium text-gray-700">
                    Week block
                  </label>
                  <select
                    id="import-schedule-week"
                    value={selectedWeekBlockKey}
                    onChange={(e) => onWeekBlockChange(e.target.value)}
                    className="min-w-[12rem] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {gridWeekBlocks.map((b) => (
                      <option key={b.label} value={b.label}>
                        {formatCompanyDateRange(b.weekStart, b.weekEnd) || b.label}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <span className="text-xs text-gray-500">
                  {step === 'grid-confirm' ? 'Detected weekly rota grid.' : 'Columns and rows update from the selected sheet.'}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload an Excel file (.xlsx or .xls). Supports flat columns (Employee, Date, Start, End) or weekly rota
                grids (staff rows with Mon–Sun shift times). Names can be employees or placeholder seats; missing
                placeholders are created on this schedule when you confirm import. By default only the current
                scheduling week is imported; if the file contains other weeks you can review and include them.
              </p>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 hover:bg-gray-100">
                <Upload className="mb-2 h-10 w-10 text-gray-400" />
                <span className="text-sm font-medium text-blue-600">Choose Excel file</span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) parseFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          )}

          {step === 'grid-confirm' && importPattern && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
                <p className="font-medium">Detected: {importPattern.patternName}</p>
                <p className="mt-1 text-blue-800">
                  File: <span className="font-medium">{fileName}</span> — {sheetRows.length} shift(s) for{' '}
                  {allowedWeekLabel || 'the selected week'}.
                </p>
              </div>

              {sheetRows.length > 0 ? (
                <div className="max-h-48 overflow-auto rounded border border-gray-200 text-xs">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Employee</th>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Start</th>
                        <th className="px-2 py-1 text-left">End</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheetRows.slice(0, 12).map((r) => (
                        <tr key={r.rowNumber} className="border-t border-gray-100">
                          <td className="px-2 py-1">{r.cells['Employee']}</td>
                          <td className="px-2 py-1">{formatImportDateDisplay(r.cells['Date'])}</td>
                          <td className="px-2 py-1">{r.cells['Start time']}</td>
                          <td className="px-2 py-1">{r.cells['End time']}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sheetRows.length > 12 ? (
                    <p className="border-t border-gray-100 px-2 py-1 text-gray-500">
                      …and {sheetRows.length - 12} more
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-amber-800">
                  No shifts found for this week in the selected sheet. Try another week block or sheet, or use manual
                  column mapping.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStep('upload')}
                  className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const data = sheetsByName[selectedSheetName];
                    if (data) {
                      setHeaders(data.headers);
                      setImportPattern(null);
                      const stored = loadStoredMapping(locationId, scheduleId);
                      setMapping(intersectMappingWithHeaders(stored, data.headers));
                    }
                    setStep('mapping');
                  }}
                  className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Manual column mapping
                </button>
                <button
                  type="button"
                  onClick={() => void handleValidate()}
                  disabled={validating || sheetRows.length === 0}
                  className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {validating ? 'Validating…' : 'Validate import'}
                </button>
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                File: <span className="font-medium">{fileName}</span> — {sheetRows.length} non-empty data row(s). Mappings
                are remembered for this location and schedule.
              </p>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4">Staffly field</th>
                    <th className="py-2">Excel column</th>
                  </tr>
                </thead>
                <tbody>
                  {MAPPING_FIELDS.map(({ key, label, required }) => (
                    <tr key={key} className="border-b border-gray-100">
                      <td className="py-2 pr-4 align-middle">
                        {label}
                        {required && <span className="text-red-500"> *</span>}
                      </td>
                      <td className="py-2 align-middle">
                        <select
                          value={mapping[key] || ''}
                          onChange={(e) =>
                            setMapping((m) => ({
                              ...m,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-full max-w-xs rounded border border-gray-300 px-2 py-1.5"
                        >
                          {headerOptions.map((opt) => (
                            <option key={opt || '—'} value={opt}>
                              {opt || '(none)'}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                type="button"
                onClick={() => void handleValidate()}
                disabled={validating}
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {validating ? 'Validating…' : 'Validate import'}
              </button>
            </div>
          )}

          {step === 'multi-week-confirm' && multiWeekSummary && (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
                <p className="font-medium">This file contains shifts outside the current week</p>
                <p className="mt-1">
                  Current week ({allowedWeekLabel}):{' '}
                  <span className="font-semibold">{multiWeekSummary.currentWeekCount}</span> shift(s). Additional weeks
                  in the file are listed below.
                </p>
              </div>

              <div className="space-y-3">
                {multiWeekSummary.otherWeeks.map((week) => (
                  <div key={week.label} className="rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCompanyDateRange(week.weekStart, week.weekEnd) || week.label}
                      </p>
                      <span className="text-sm text-gray-600">{week.shiftCount} shift(s)</span>
                    </div>
                    {week.sampleRows.length > 0 ? (
                      <div className="max-h-36 overflow-auto text-xs">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr>
                              <th className="px-2 py-1 text-left">Employee</th>
                              <th className="px-2 py-1 text-left">Date</th>
                              <th className="px-2 py-1 text-left">Start</th>
                              <th className="px-2 py-1 text-left">End</th>
                            </tr>
                          </thead>
                          <tbody>
                            {week.sampleRows.map((r) => (
                              <tr key={r.rowNumber} className="border-t border-gray-100">
                                <td className="px-2 py-1">{r.cells[mapping.employeeName] ?? r.cells['Employee']}</td>
                                <td className="px-2 py-1">
                                  {formatImportDateDisplay(r.cells[mapping.date] ?? r.cells['Date'])}
                                </td>
                                <td className="px-2 py-1">{r.cells[mapping.startTime] ?? r.cells['Start time']}</td>
                                <td className="px-2 py-1">{r.cells[mapping.endTime] ?? r.cells['End time']}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {week.shiftCount > week.sampleRows.length ? (
                          <p className="border-t border-gray-100 px-2 py-1 text-gray-500">
                            …and {week.shiftCount - week.sampleRows.length} more in this week
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <p className="text-sm text-gray-600">
                Choose whether to import only the current scheduling week or all weeks found in this file (
                {formatCompanyDateRange(
                  multiWeekSummary.fullRangeStart,
                  multiWeekSummary.fullRangeEnd
                )}
                ).
              </p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStep(importPattern ? 'grid-confirm' : 'mapping')}
                  className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => void handleMultiWeekChoice('current-week')}
                  disabled={validating || multiWeekSummary.currentWeekCount === 0}
                  className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  {validating && importScope === 'current-week' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Import current week only ({multiWeekSummary.currentWeekCount})
                </button>
                <button
                  type="button"
                  onClick={() => void handleMultiWeekChoice('all-weeks')}
                  disabled={validating}
                  className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {validating && importScope === 'all-weeks' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Import all weeks ({multiWeekSummary.allRows.length})
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && validateResult && (
            <div className="space-y-4">
              {importScope === 'all-weeks' && multiWeekSummary ? (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  Importing shifts for all weeks in the file (
                  {formatCompanyDateRange(
                    multiWeekSummary.fullRangeStart,
                    multiWeekSummary.fullRangeEnd
                  )}
                  ).
                </div>
              ) : null}
              {existingShiftCount != null && existingShiftCount > 0 ? (
                <p className="text-sm text-gray-600">
                  This week already has{' '}
                  <span className="font-medium">{existingShiftCount}</span> shift(s) on this schedule. Imports add new shifts
                  or adjust overlaps per your choice below; the week is not cleared up front.
                </p>
              ) : null}

              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <p>
                  Batch #{validateResult.batchId} — Valid:{' '}
                  <span className="font-semibold text-green-700">{validateResult.validRowCount}</span> — Overlap:{' '}
                  <span className="font-semibold text-amber-700">{validateResult.overlapRowCount ?? 0}</span> — Holiday:{' '}
                  <span className="font-semibold text-green-800">
                    {validateResult.rows.filter((row) => row.status === 'Holiday').length}
                  </span>{' '}
                  — Invalid:{' '}
                  <span className="font-semibold text-red-700">{validateResult.invalidRowCount}</span>
                </p>
              </div>

              <ScheduleShiftImportRowsPreviewTable rows={validateResult.rows} />

              <fieldset className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2.5">
                <legend className="px-1 text-sm font-medium text-amber-900">
                  When a row overlaps another import row or an existing shift
                </legend>
                <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                    <input
                      type="radio"
                      name="overlap-resolution"
                      className="text-blue-600"
                      checked={overlapResolution === 'Skip'}
                      onChange={() => setOverlapResolution('Skip')}
                      disabled={committing}
                    />
                    <span>
                      <strong>Skip</strong> — do not import overlapping rows
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                    <input
                      type="radio"
                      name="overlap-resolution"
                      className="text-blue-600"
                      checked={overlapResolution === 'Overwrite'}
                      onChange={() => setOverlapResolution('Overwrite')}
                      disabled={committing}
                    />
                    <span>
                      <strong>Overwrite</strong> — remove conflicting existing shifts (and resolve import clashes in row order),
                      then import
                    </span>
                  </label>
                </div>
              </fieldset>

              <p className="text-sm text-gray-600">
                Invalid and holiday/day-off rows are never imported. With <strong>Skip</strong>, only non-overlapping rows are imported. With{' '}
                <strong>Overwrite</strong>, overlapping rows replace conflicting shifts as described above.
              </p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setStep(
                      multiWeekSummary?.hasOtherWeeks
                        ? 'multi-week-confirm'
                        : importPattern
                          ? 'grid-confirm'
                          : 'mapping'
                    )
                  }
                  className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => void handleCommit()}
                  disabled={committing || previewImportableCount === 0}
                  className="inline-flex min-w-[10rem] items-center justify-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {committing ? 'Importing…' : 'Confirm import'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-3 text-center">
              <CheckCircle className="mx-auto h-14 w-14 text-green-500" />
              <p className="text-lg font-medium text-gray-900">Import finished</p>
              <p className="text-sm text-gray-600">
                Shifts were created for valid rows. View history under{' '}
                <strong>Scheduling → View Import History</strong> (or open View Import History from the Actions menu).
              </p>
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportScheduleFromFileModal;
